import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Eye, CheckCircle2, Copy, Route, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import roteiroService, { type Roteiro, type RoteiroOperacao } from '@/services/roteiro';
import produtoService, { type Produto } from '@/services/produto';
import centroTrabalhoService, { type CentroTrabalho } from '@/services/centroTrabalho';

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  APROVADO: 'Aprovado',
  OBSOLETO: 'Obsoleto',
};
const STATUS_TONES: Record<string, 'neutral' | 'success' | 'danger'> = {
  RASCUNHO: 'neutral',
  APROVADO: 'success',
  OBSOLETO: 'danger',
};

const TIPOS_PRODUTO_ACABADO = ['PA', '04'];

const FORM_VAZIO = {
  id: undefined as string | undefined,
  produto_id: '',
  versao: '',
  tempo_total_padrao: 0,
  status: 'RASCUNHO',
  operacoes: [] as RoteiroOperacao[],
};

function formatTempo(minutos?: number | null) {
  if (!minutos) return '0 min';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return h > 0 ? `${h}h ${m}min` : `${m} min`;
}

export default function RoteiroPage() {
  const navigate = useNavigate();
  const [roteiros, setRoteiros] = useState<Roteiro[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [centros, setCentros] = useState<CentroTrabalho[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [dialog, setDialog] = useState(false);
  const [somenteVisualizar, setSomenteVisualizar] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);

  const [dialogNovaVersao, setDialogNovaVersao] = useState(false);
  const [roteiroParaNovaVersao, setRoteiroParaNovaVersao] = useState<Roteiro | null>(null);
  const [novaVersaoTexto, setNovaVersaoTexto] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await roteiroService.listar({ status: filtroStatus || undefined });
      setRoteiros(data);
    } catch (error) {
      console.error('Erro ao carregar roteiros', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
    centroTrabalhoService.listar().then(setCentros).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  const produtosPA = useMemo(
    () => produtos.filter((p) => TIPOS_PRODUTO_ACABADO.includes(p.tipo_produto_codigo || '')),
    [produtos]
  );

  const roteirosFiltrados = useMemo(() => {
    if (!busca) return roteiros;
    const termo = busca.toLowerCase();
    return roteiros.filter(
      (r) => (r.produto_nome || '').toLowerCase().includes(termo) || (r.produto_codigo || '').toLowerCase().includes(termo)
    );
  }, [roteiros, busca]);

  const tempoTotalCalculado = useMemo(
    () => form.operacoes.reduce((total, op) => total + (Number(op.tempo_padrao_minutos) || 0) + (Number(op.tempo_setup_minutos) || 0), 0),
    [form.operacoes]
  );

  const abrirDialog = () => {
    setForm(FORM_VAZIO);
    setSomenteVisualizar(false);
    setDialog(true);
  };

  const editar = (item: Roteiro) => {
    setForm({
      id: item.id,
      produto_id: item.produto_id,
      versao: item.versao,
      tempo_total_padrao: item.tempo_total_padrao ?? 0,
      status: item.status,
      operacoes: item.operacoes || [],
    });
    setSomenteVisualizar(false);
    setDialog(true);
  };

  const visualizar = (item: Roteiro) => {
    editar(item);
    setSomenteVisualizar(true);
  };

  const adicionarOperacao = () => {
    setForm({
      ...form,
      operacoes: [
        ...form.operacoes,
        { sequencia: (form.operacoes.length + 1) * 10, operacao: '', centro_trabalho_id: null, tempo_padrao_minutos: 0, tempo_setup_minutos: 0 },
      ],
    });
  };

  const atualizarOperacao = (index: number, patch: Partial<RoteiroOperacao>) => {
    const novas = [...form.operacoes];
    novas[index] = { ...novas[index], ...patch };
    setForm({ ...form, operacoes: novas });
  };

  const removerOperacao = (index: number) => {
    setForm({ ...form, operacoes: form.operacoes.filter((_, i) => i !== index) });
  };

  const salvar = async () => {
    if (!form.produto_id || !form.versao) return;
    setSalvando(true);
    try {
      const payload = {
        produto_id: form.produto_id,
        versao: form.versao,
        tempo_total_padrao: tempoTotalCalculado,
        operacoes: form.operacoes.map((op) => ({
          sequencia: Number(op.sequencia),
          operacao: op.operacao,
          centro_trabalho_id: op.centro_trabalho_id || null,
          tempo_padrao_minutos: Number(op.tempo_padrao_minutos) || 0,
          tempo_setup_minutos: Number(op.tempo_setup_minutos) || 0,
        })),
      };
      if (form.id) {
        await roteiroService.atualizar(form.id, payload);
      } else {
        await roteiroService.criar(payload);
      }
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar roteiro', error);
    } finally {
      setSalvando(false);
    }
  };

  const aprovar = async (item: Roteiro) => {
    try {
      await roteiroService.aprovar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao aprovar roteiro', error);
    }
  };

  const abrirNovaVersao = (item: Roteiro) => {
    setRoteiroParaNovaVersao(item);
    setNovaVersaoTexto('');
    setDialogNovaVersao(true);
  };

  const confirmarNovaVersao = async () => {
    if (!roteiroParaNovaVersao || !novaVersaoTexto) return;
    try {
      await roteiroService.novaVersao(roteiroParaNovaVersao.id, novaVersaoTexto);
      setDialogNovaVersao(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao criar nova versão', error);
    }
  };

  const deletar = async (item: Roteiro) => {
    try {
      await roteiroService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir roteiro', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Route size={21} strokeWidth={2} />}
        title="Roteiros de Produção"
        description="Sequência de operações e centros de trabalho utilizados na fabricação de cada produto."
        actions={
          <Button type="button" onClick={() => navigate('/producao/roteiros/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Roteiro
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por produto..."
                />
              </div>
              <Select filter value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Todos os status</option>
                <option value="RASCUNHO">Rascunho</option>
                <option value="APROVADO">Aprovado</option>
                <option value="OBSOLETO">Obsoleto</option>
              </Select>
            </div>
          }
        />

        {!loading && roteirosFiltrados.length === 0 ? (
          <EmptyState message="Nenhum roteiro encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Versão</th>
                  <th>Status</th>
                  <th>Tempo Total</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  roteirosFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <p>{item.produto_nome}</p>
                        <span>{item.produto_codigo}</span>
                      </td>
                      <td>v{item.versao}</td>
                      <td>
                        <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONES[item.status]} />
                      </td>
                      <td>{formatTempo(item.tempo_total_padrao)}</td>
                      <td className="nx-col-acoes">
                        <IconButton label="Visualizar" onClick={() => visualizar(item)}>
                          <Eye size={15} strokeWidth={1.9} aria-hidden />
                        </IconButton>
                        <IconButton
                          label="Aprovar"
                          onClick={() => aprovar(item)}
                          disabled={item.status !== 'RASCUNHO'}
                        >
                          <CheckCircle2 size={15} strokeWidth={1.9} aria-hidden />
                        </IconButton>
                        <IconButton label="Nova Versão" onClick={() => abrirNovaVersao(item)}>
                          <Copy size={15} strokeWidth={1.9} aria-hidden />
                        </IconButton>
                        <TableActions
                          onEdit={() => navigate(`/producao/roteiros/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          disabled={item.status === 'APROVADO'}
                          deleteTitle="Excluir roteiro"
                          deleteDescription={`Tem certeza de que deseja excluir o roteiro de "${item.produto_nome}" (v${item.versao})? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>
              {somenteVisualizar ? 'Visualizar Roteiro' : form.id ? 'Editar Roteiro' : 'Novo Roteiro'}
            </DialogTitle>
            <DialogDescription>
              {somenteVisualizar
                ? 'Consulte os dados e as operações do roteiro.'
                : form.id
                  ? 'Atualize os dados e as operações do roteiro.'
                  : 'Preencha os dados para incluir um novo roteiro.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <fieldset disabled={somenteVisualizar} style={{ border: 0, padding: 0, margin: 0 }}>
              <div className="nx-form-grid">
                <Field label="Produto *" htmlFor="roteiro-produto" className="nx-sngpc-field--full">
                  <Select
                    id="roteiro-produto"
                    value={form.produto_id}
                    onChange={(e) => setForm({ ...form, produto_id: e.target.value })}
                    disabled={!!form.id}
                    required
                  >
                    <option value="">Selecione</option>
                    {produtosPA.map((p) => (
                      <option key={p.id} value={p.id}>{p.codigo} - {p.descricao}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Versão *" htmlFor="roteiro-versao">
                  <Input
                    id="roteiro-versao"
                    value={form.versao}
                    onChange={(e) => setForm({ ...form, versao: e.target.value })}
                    placeholder="1.0"
                    required
                  />
                </Field>
                <Field label="Tempo Total (min)" htmlFor="roteiro-tempo-total">
                  <Input id="roteiro-tempo-total" value={tempoTotalCalculado} readOnly />
                </Field>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '1.25rem 0 0.75rem',
                }}
              >
                <strong>Operações</strong>
                {!somenteVisualizar && (
                  <Button type="button" variant="ghost" onClick={adicionarOperacao}>
                    <Plus size={15} strokeWidth={2.2} />
                    Adicionar
                  </Button>
                )}
              </div>

              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Seq</th>
                      <th>Operação *</th>
                      <th>Centro de Trabalho</th>
                      <th>Tempo (min) *</th>
                      <th>Setup (min)</th>
                      <th className="nx-col-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.operacoes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="nx-table-loading">Nenhuma operação adicionada</td>
                      </tr>
                    ) : (
                      form.operacoes.map((op, index) => (
                        <tr key={index}>
                          <td>
                            <Input
                              type="number"
                              value={op.sequencia}
                              onChange={(e) => atualizarOperacao(index, { sequencia: Number(e.target.value) })}
                            />
                          </td>
                          <td>
                            <Input
                              value={op.operacao}
                              onChange={(e) => atualizarOperacao(index, { operacao: e.target.value })}
                              placeholder="Ex: Pesagem"
                            />
                          </td>
                          <td>
                            <Select
                              value={op.centro_trabalho_id || ''}
                              onChange={(e) => atualizarOperacao(index, { centro_trabalho_id: e.target.value || null })}
                            >
                              <option value="">-</option>
                              {centros.map((c) => (
                                <option key={c.id} value={c.id}>{c.codigo} - {c.descricao}</option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <DecimalInput
                              value={op.tempo_padrao_minutos ?? ''}
                              onValueChange={(value) => atualizarOperacao(index, { tempo_padrao_minutos: value ?? 0 })}
                            />
                          </td>
                          <td>
                            <DecimalInput
                              value={op.tempo_setup_minutos ?? ''}
                              onValueChange={(value) => atualizarOperacao(index, { tempo_setup_minutos: value ?? 0 })}
                            />
                          </td>
                          <td className="nx-col-acoes">
                            {!somenteVisualizar && (
                              <IconButton label="Remover" variant="danger" onClick={() => removerOperacao(index)}>
                                <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                              </IconButton>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </fieldset>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>
                {somenteVisualizar ? 'Fechar' : 'Cancelar'}
              </Button>
            </DialogClose>
            {!somenteVisualizar && (
              <Button type="submit" loading={salvando} loadingText="Salvando...">
                <Save size={16} /> Salvar
              </Button>
            )}
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogNovaVersao} onOpenChange={setDialogNovaVersao}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Versão</DialogTitle>
            <DialogDescription>
              Nova versão para {roteiroParaNovaVersao?.produto_nome}
            </DialogDescription>
          </DialogHeader>
          <Field label="Versão *" htmlFor="roteiro-nova-versao">
            <Input
              id="roteiro-nova-versao"
              value={novaVersaoTexto}
              onChange={(e) => setNovaVersaoTexto(e.target.value)}
              placeholder="2.0"
            />
          </Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={confirmarNovaVersao}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
